/**
 * Quota Management — per-product, per-channel limits from platform licensing.
 * Summary cards, search + filter chips, product cards (licensed vs greyed
 * channels, exceeded state), row → UsageDetailDrawer. Ported from the console.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable, TextInput, StyleSheet } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import Icon, { IconName } from '../components/notif/Icon';
import { PageHeader, EmptyState, Btn } from '../components/notif/ui';
import UsageDetailDrawer, { DrawerProduct, ChannelQuota } from '../components/notif/usageDetailDrawer';
import { getAllUsage } from '../services/notification/usage';
import { handleApiError } from '../utils/errorHandler';
import { useT } from '../i18n/useT';
import { useDirection } from '../lib/rtl';
import type { BackendProductUsageRow, BackendQuotaUsage } from '../schema/notification';

function fmtResets(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function toQuota(c: BackendQuotaUsage | undefined): ChannelQuota {
  return {
    used: c?.reserved ?? 0, limit: c?.limit ?? 0, reserved: c?.reserved ?? 0,
    confirmed: c?.confirmed ?? 0, released: c?.released ?? 0,
    resets: c ? fmtResets(c.resetsAt) : '—', licensed: !!c && c.limit > 0,
  };
}
function mapProduct(row: BackendProductUsageRow): DrawerProduct {
  const by = Object.fromEntries(row.channels.map(c => [c.channel, c]));
  return {
    id: row.productId, name: row.displayName || row.productId, period: 'Current period',
    channels: { email: toQuota(by.email), whatsapp: toQuota(by.whatsapp), sms: toQuota(by.sms), push: toQuota(by.push) },
    history: [],
  };
}

const SummaryCard: React.FC<{ label: string; value: number; sub: string; tone?: 'success' | 'warn' | 'error' }> = ({ label, value, sub, tone }) => {
  const { theme: { colors } } = useTheme();
  const color = tone === 'success' ? colors.success : tone === 'warn' ? colors.warn : tone === 'error' ? colors.danger : colors.ink;
  return (
    <View style={[q.stat, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <Text style={{ color: colors.ink3, fontSize: 12 }}>{label}</Text>
      <Text style={{ color, fontSize: 28, fontWeight: '700', marginVertical: 2 }}>{value}</Text>
      <Text style={{ color: colors.ink4, fontSize: 11 }}>{sub}</Text>
    </View>
  );
};

const QuotaBar: React.FC<{ label: string; icon: IconName; c: ChannelQuota }> = ({ label, icon, c }) => {
  const { theme: { colors, font } } = useTheme();
  const t = useT();
  const { isRTL } = useDirection();
  // RN's TextStyle has no logical 'end' value for textAlign — compute the
  // physical side from direction explicitly instead.
  const endAlign = isRTL ? 'left' : 'right';
  if (!c.licensed) {
    return (
      <View style={q.chRow}>
        <View style={q.chLabel}><Icon name={icon} size={13} color={colors.ink4} /><Text style={{ color: colors.ink4, fontSize: 12 }}>{label}</Text></View>
        <View style={[q.bar, { backgroundColor: colors.paper3 }]} />
        <Text style={{ color: colors.ink4, fontSize: 11, width: 120, textAlign: endAlign }}>{t('quotas.notLicensed')}</Text>
      </View>
    );
  }
  const pct = c.limit > 0 ? (c.used / c.limit) * 100 : 0;
  const exceeded = c.used >= c.limit;
  const warn = pct >= 80 && !exceeded;
  const fill = exceeded ? colors.danger : warn ? colors.warn : colors.success;
  return (
    <View style={q.chRow}>
      <View style={q.chLabel}><Icon name={icon} size={13} color={colors.ink3} /><Text style={{ color: colors.ink2, fontSize: 12 }}>{label}</Text></View>
      <View style={[q.bar, { backgroundColor: colors.paper3 }]}>
        <View style={{ width: `${Math.min(100, pct)}%`, height: '100%', backgroundColor: fill, borderRadius: 2 }} />
      </View>
      <Text style={{ color: exceeded ? colors.danger : colors.ink3, fontSize: 11, width: 120, textAlign: endAlign, fontFamily: font.mono }}>
        {c.used.toLocaleString()}/{c.limit.toLocaleString()}
      </Text>
    </View>
  );
};

export default function QuotasScreen() {
  const { theme: { colors, font } } = useTheme();
  const { pad } = useResponsive();
  const t = useT();
  const CH: Array<{ key: keyof DrawerProduct['channels']; label: string; icon: IconName }> = [
    { key: 'email', label: t('quotas.channel.email'), icon: 'mail' },
    { key: 'whatsapp', label: t('quotas.channel.whatsapp'), icon: 'message' },
    { key: 'sms', label: t('quotas.channel.sms'), icon: 'phone' },
    { key: 'push', label: t('quotas.channel.push'), icon: 'bell' },
  ];
  const [products, setProducts] = useState<DrawerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'warn' | 'exceeded'>('all');
  const [selected, setSelected] = useState<DrawerProduct | null>(null);

  const load = () => {
    setLoading(true); setErr(false);
    getAllUsage()
      .then(d => setProducts((d.products ?? []).map(mapProduct)))
      .catch(e => { setErr(true); handleApiError(e, t('quotas.errLoad')); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => products.filter(p => {
    if (search) {
      const s = search.toLowerCase();
      if (!(p.name.toLowerCase().includes(s) || p.id.toLowerCase().includes(s))) return false;
    }
    if (filter === 'all') return true;
    const cs = (Object.values(p.channels) as ChannelQuota[]).filter(c => c.licensed);
    if (filter === 'exceeded') return cs.some(c => c.used >= c.limit);
    return cs.some(c => (c.used / c.limit) * 100 >= 80 && c.used < c.limit);
  }), [products, search, filter]);

  const summary = useMemo(() => {
    let exceeded = 0, near = 0, healthy = 0;
    products.forEach(p => {
      const cs = (Object.values(p.channels) as ChannelQuota[]).filter(c => c.licensed);
      if (cs.some(c => c.used >= c.limit)) exceeded++;
      else if (cs.some(c => (c.used / c.limit) * 100 >= 80)) near++;
      else healthy++;
    });
    return { total: products.length, exceeded, near, healthy };
  }, [products]);

  return (
    <View style={{ flex: 1 }}>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: pad, gap: 16 }}>
        <PageHeader
          title={t('quotas.title')}
          subtitle={t('quotas.subtitle')}
        />
        <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
          <SummaryCard label={t('quotas.products')} value={summary.total} sub={t('quotas.acrossPlatform')} />
          <SummaryCard label={t('quotas.healthy')} value={summary.healthy} sub={t('quotas.under80')} tone="success" />
          <SummaryCard label={t('quotas.nearLimit')} value={summary.near} sub={t('quotas.between80and99')} tone="warn" />
          <SummaryCard label={t('quotas.quotaExceeded')} value={summary.exceeded} sub={t('quotas.blockingNewSends')} tone="error" />
        </View>

        <View style={[q.filterRow, { backgroundColor: colors.card, borderColor: colors.line }]}>
          <View style={[q.search, { borderColor: colors.line, backgroundColor: colors.paper }]}>
            <Icon name="search" size={14} color={colors.ink3} />
            <TextInput value={search} onChangeText={setSearch} placeholder={t('quotas.searchPlaceholder')} placeholderTextColor={colors.ink4} style={[q.searchInput, { color: colors.ink }]} />
          </View>
          {(['all', 'warn', 'exceeded'] as const).map(f => (
            <Pressable key={f} onPress={() => setFilter(f)} style={[q.chip, { borderColor: filter === f ? colors.accent : colors.line, backgroundColor: filter === f ? colors.accentSoft : 'transparent' }]}>
              <Text style={{ color: filter === f ? colors.accentInk : colors.ink3, fontSize: 12 }}>{f === 'all' ? t('quotas.filterAll') : f === 'warn' ? t('quotas.filterWarn') : t('quotas.filterExceeded')}</Text>
            </Pressable>
          ))}
          <View style={{ flex: 1 }} />
          <Text style={{ color: colors.ink3, fontSize: 12, fontFamily: font.mono }}>{t('quotas.countOfTotal', { count: filtered.length, total: products.length })}</Text>
        </View>

        {loading && <EmptyState title={t('quotas.loading')} body={t('quotas.loadingBody')} />}
        {err && !loading && <EmptyState icon="alert" title={t('quotas.loadError')} body={t('quotas.loadErrorBody')} action={<Btn label={t('common.retry')} onPress={load} />} />}
        {!loading && !err && filtered.map(p => {
          const exceeded = (Object.values(p.channels) as ChannelQuota[]).some(c => c.licensed && c.used >= c.limit);
          return (
            <Pressable key={p.id} onPress={() => setSelected(p)} style={[q.card, { backgroundColor: colors.card, borderColor: exceeded ? colors.dangerLine : colors.line }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: colors.ink, fontSize: 15, fontWeight: '600' }}>{p.name}</Text>
                  <Text style={{ color: colors.ink4, fontSize: 12, fontFamily: font.mono }}>{p.id}</Text>
                </View>
                <View style={[q.pill, { backgroundColor: colors.successSoft, borderColor: colors.successLine }]}><Text style={{ color: colors.success, fontSize: 11 }}>{t('quotas.active')}</Text></View>
                {exceeded && <View style={[q.pill, { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine }]}><Text style={{ color: colors.danger, fontSize: 11 }}>{t('quotas.quotaExceededPill')}</Text></View>}
              </View>
              {CH.map(c => <QuotaBar key={c.key} label={c.label} icon={c.icon} c={p.channels[c.key]} />)}
            </Pressable>
          );
        })}
        {!loading && !err && filtered.length === 0 && (
          <EmptyState icon="search" title={products.length === 0 ? t('quotas.emptyNoProducts') : t('quotas.emptyNoMatch')} body={products.length === 0 ? t('quotas.emptyNoProductsBody') : t('quotas.emptyNoMatchBody')} />
        )}
      </ScrollView>

      {selected && <UsageDetailDrawer product={selected} onClose={() => setSelected(null)} />}
    </View>
  );
}

const q = StyleSheet.create({
  stat: { flex: 1, minWidth: 160, padding: 16, borderRadius: 2, borderWidth: 1 },
  filterRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 2, borderWidth: 1, flexWrap: 'wrap' },
  search: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, height: 34, borderRadius: 2, borderWidth: 1, flex: 1, minWidth: 220, maxWidth: 360 },
  searchInput: { flex: 1, fontSize: 13, outlineStyle: 'none' as any },
  chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 2, borderWidth: 1 },
  card: { padding: 16, borderRadius: 2, borderWidth: 1 },
  pill: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 2, borderWidth: 1 },
  chRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 8 },
  chLabel: { flexDirection: 'row', alignItems: 'center', gap: 6, width: 100 },
  bar: { flex: 1, height: 8, borderRadius: 2, overflow: 'hidden' },
});
